import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Invoice, ESTADO_LABELS, ESTADO_COLORS } from './facturas/types';
import InvoiceForm from './facturas/InvoiceForm';
import InvoicePreview from './facturas/InvoicePreview';
import { useSettings } from '@/hooks/useSettings';

type View = 'list' | 'create' | 'edit' | 'preview';

const STORAGE_KEY = 'taller_kevin_facturas';

const FacturasTab = () => {
  const { settings } = useSettings();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [view, setView] = useState<View>('list');
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState<string>('all');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Load from Supabase settings table
  const loadInvoices = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'facturas')
        .maybeSingle();
      if (data?.value) {
        setInvoices(data.value as Invoice[]);
      } else {
        // fallback localStorage
        const local = localStorage.getItem(STORAGE_KEY);
        if (local) setInvoices(JSON.parse(local));
      }
    } catch {
      const local = localStorage.getItem(STORAGE_KEY);
      if (local) setInvoices(JSON.parse(local));
    }
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const saveInvoices = async (list: Invoice[]) => {
    setSaving(true);
    try {
      await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .upsert({ key: 'facturas', value: list }, { onConflict: 'key' });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (inv: Invoice) => {
    const exists = invoices.find((f) => f.id === inv.id);
    const updated = exists
      ? invoices.map((f) => (f.id === inv.id ? inv : f))
      : [inv, ...invoices];
    setInvoices(updated);
    await saveInvoices(updated);
    setSelected(inv);
    setView('preview');
  };

  const handleDelete = async (id: string) => {
    const updated = invoices.filter((f) => f.id !== id);
    setInvoices(updated);
    await saveInvoices(updated);
    setDeleteConfirm(null);
    if (selected?.id === id) { setSelected(null); setView('list'); }
  };

  const filtered = invoices.filter((f) => {
    const matchSearch = !search || f.numero.toLowerCase().includes(search.toLowerCase()) || f.cliente_nombre.toLowerCase().includes(search.toLowerCase());
    const matchEstado = filterEstado === 'all' || f.estado === filterEstado;
    return matchSearch && matchEstado;
  });

  const totalPendiente = invoices.filter((f) => f.estado === 'emitida').reduce((a, f) => a + f.total, 0);
  const totalPagado = invoices.filter((f) => f.estado === 'pagada').reduce((a, f) => a + f.total, 0);

  const tallerProps = {
    tallerNombre: settings.nombre_taller,
    tallerNif: '',
    tallerDireccion: settings.direccion,
    tallerEmail: settings.email,
    tallerTelefono: settings.telefono,
  };

  if (view === 'create') {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setView('list')} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer transition-colors">
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <h2 className="text-white font-black text-lg">Nueva factura</h2>
        </div>
        <InvoiceForm
          allInvoices={invoices}
          {...tallerProps}
          onSave={handleSave}
          onCancel={() => setView('list')}
        />
      </div>
    );
  }

  if (view === 'edit' && selected) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setView('preview')} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer transition-colors">
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <h2 className="text-white font-black text-lg">Editar {selected.numero}</h2>
        </div>
        <InvoiceForm
          invoice={selected}
          allInvoices={invoices}
          {...tallerProps}
          onSave={handleSave}
          onCancel={() => setView('preview')}
        />
      </div>
    );
  }

  if (view === 'preview' && selected) {
    return (
      <InvoicePreview
        invoice={selected}
        {...tallerProps}
        onClose={() => setView('list')}
        onEdit={() => setView('edit')}
      />
    );
  }

  // LIST VIEW
  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total facturas', value: invoices.length, icon: 'ri-file-text-line', color: 'text-zinc-300' },
          { label: 'Emitidas', value: invoices.filter((f) => f.estado === 'emitida').length, icon: 'ri-send-plane-line', color: 'text-yellow-400' },
          { label: 'Pendiente cobro', value: `${totalPendiente.toFixed(0)} €`, icon: 'ri-time-line', color: 'text-orange-400' },
          { label: 'Total cobrado', value: `${totalPagado.toFixed(0)} €`, icon: 'ri-checkbox-circle-line', color: 'text-green-400' },
        ].map((s) => (
          <div key={s.label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${s.icon} ${s.color} text-sm`} />
              </div>
              <span className="text-zinc-500 text-xs">{s.label}</span>
            </div>
            <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm" />
            <input
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm pl-9 pr-3 py-2 rounded-lg focus:outline-none focus:border-yellow-400/60 placeholder-zinc-600"
              placeholder="Buscar factura o cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-yellow-400/60 cursor-pointer"
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
          >
            <option value="all">Todos los estados</option>
            <option value="borrador">Borrador</option>
            <option value="emitida">Emitida</option>
            <option value="pagada">Pagada</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>
        <button
          onClick={() => setView('create')}
          className="flex items-center gap-2 px-5 py-2.5 bg-yellow-400 text-black text-sm font-black rounded-lg hover:bg-yellow-300 cursor-pointer transition-all whitespace-nowrap"
        >
          <i className="ri-add-line" /> Nueva factura
        </button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
            <i className="ri-file-text-line text-zinc-600 text-4xl" />
          </div>
          <p className="text-zinc-500 text-sm">
            {invoices.length === 0 ? 'Aún no hay facturas. ¡Crea la primera!' : 'No hay resultados para tu búsqueda.'}
          </p>
          {invoices.length === 0 && (
            <button
              onClick={() => setView('create')}
              className="mt-4 px-5 py-2.5 bg-yellow-400 text-black text-sm font-black rounded-lg hover:bg-yellow-300 cursor-pointer transition-all whitespace-nowrap"
            >
              <i className="ri-add-line mr-1" /> Crear primera factura
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((inv) => (
            <div
              key={inv.id}
              className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 flex items-center gap-4 hover:border-zinc-700 transition-all group"
            >
              {/* Icon */}
              <div className="w-10 h-10 flex items-center justify-center bg-zinc-800 rounded-lg flex-shrink-0">
                <i className="ri-file-text-line text-zinc-400 text-base" />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-bold text-sm">{inv.numero}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${ESTADO_COLORS[inv.estado]}`}>
                    {ESTADO_LABELS[inv.estado]}
                  </span>
                </div>
                <p className="text-zinc-400 text-xs truncate mt-0.5">{inv.cliente_nombre}</p>
              </div>

              {/* Date */}
              <div className="hidden sm:block text-right flex-shrink-0">
                <p className="text-zinc-400 text-xs">{inv.fecha.split('-').reverse().join('/')}</p>
                <p className="text-zinc-600 text-[10px]">Vence: {inv.fecha_vencimiento.split('-').reverse().join('/')}</p>
              </div>

              {/* Total */}
              <div className="text-right flex-shrink-0">
                <p className="text-white font-black text-base">{inv.total.toFixed(2)} €</p>
                <p className="text-zinc-600 text-[10px]">{inv.lineas.length} concepto{inv.lineas.length !== 1 ? 's' : ''}</p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => { setSelected(inv); setView('preview'); }}
                  className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-yellow-400 cursor-pointer transition-colors"
                  title="Ver / Descargar PDF"
                >
                  <i className="ri-eye-line text-sm" />
                </button>
                <button
                  onClick={() => { setSelected(inv); setView('edit'); }}
                  className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white cursor-pointer transition-colors"
                  title="Editar"
                >
                  <i className="ri-edit-line text-sm" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(inv.id)}
                  className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-red-400 cursor-pointer transition-colors"
                  title="Eliminar"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {saving && (
        <div className="fixed bottom-6 right-6 bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-2 z-50">
          <i className="ri-loader-4-line animate-spin" /> Guardando...
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 flex items-center justify-center bg-red-500/10 rounded-xl mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-400 text-xl" />
            </div>
            <h3 className="text-white font-black text-center mb-2">¿Eliminar factura?</h3>
            <p className="text-zinc-500 text-sm text-center mb-5">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 border border-zinc-700 text-zinc-400 hover:text-white text-sm font-semibold rounded-lg cursor-pointer transition-all whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 bg-red-500 text-white text-sm font-black rounded-lg hover:bg-red-400 cursor-pointer transition-all whitespace-nowrap"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacturasTab;
